import React, { useState, useEffect } from 'react'
import { Link, useLocation } from "react-router-dom"
import { FaBars, FaTimes } from "react-icons/fa"
import { motion, AnimatePresence } from "motion/react"
import linkedin_logo from "../assets/logos/linkedin_logo.png"
import github_logo from "../assets/logos/github-mark/github-mark.svg"
import ThemeToggle from "./ThemeToggle"
import { useReducedMotionSafe, springs } from "../lib/motion"

const links = [
    { to: "/", label: "Home" },
    { to: "/experience", label: "Experience" },
    { to: "/projects", label: "Projects" },
]

const Navbar = () => {
    useEffect(() => { document.title = "Nathaniel Asia" }, []);

    const [click, setClick] = useState(false);
    const handleClick = () => setClick(!click);
    const closeMenu = () => setClick(false);

    // Lock background scroll while the fullscreen mobile menu is open.
    useEffect(() => {
        document.body.style.overflow = click ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [click]);

    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const changeColor = () => setScrolled(window.scrollY >= 80);
        changeColor();
        window.addEventListener("scroll", changeColor);
        return () => window.removeEventListener("scroll", changeColor);
    }, []);

    const location = useLocation();
    const notHome = location.pathname !== '/';
    const solid = scrolled || notHome;

    const reducedMotion = useReducedMotionSafe();
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: reducedMotion ? "instant" : "smooth" });
    };

    // Shared between the desktop inline nav and the mobile fullscreen menu,
    // so the two never drift out of sync with each other.
    const navLinkItems = (
        <>
            {links.map(({ to, label }) => (
                <li key={to}>
                    <Link className="hover-underline" to={to} onClick={() => { closeMenu(); scrollToTop(); }}>{label}</Link>
                </li>
            ))}
            <li><a className="hover-underline" href="#footer" onClick={closeMenu}>Contact</a></li>
            <li>
                <a href="https://github.com/NateAsia" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    <img className="icon-invert-dark h-5 w-5 opacity-80 transition-opacity hover:opacity-100" src={github_logo} alt="GitHub" />
                </a>
            </li>
            <li>
                <a href="https://www.linkedin.com/in/NathanielAsia" target="_blank" rel="noreferrer" onClick={closeMenu}>
                    <img className="h-5 w-5 opacity-80 transition-opacity hover:opacity-100" src={linkedin_logo} alt="LinkedIn" />
                </a>
            </li>
        </>
    );

    return (
        <div id="nav">
            <div className="fixed inset-x-0 top-0 z-40 h-[65px]">
                {/* Background/blur lives on its own layer, not an ancestor of the
                    mobile menu below: `backdrop-filter` on an ancestor creates a new
                    containing block for `position: fixed` descendants, which trapped
                    the fullscreen mobile menu inside this 65px bar instead of the
                    viewport whenever this bar was in its "solid" (blurred) state. */}
                <div
                    className={`absolute inset-0 border-b transition-colors duration-500 ${
                        solid
                            ? "border-ink-800 bg-ink-950/80 backdrop-blur-md"
                            : "border-transparent bg-transparent"
                    }`}
                />

                <div className="relative flex h-full items-center justify-between px-6 sm:px-10">
                    <Link to="/" onClick={scrollToTop} className="font-mono text-lg font-medium text-mist-100">
                        nathaniel<span className="text-signal-500">.</span>asia
                    </Link>

                    <div className="flex items-center gap-6 lg:gap-8">
                        <ul className="hidden items-center gap-6 font-mono text-sm text-mist-100 md:flex lg:gap-8">
                            {navLinkItems}
                        </ul>

                        {/* Persistent regardless of the mobile menu's open/closed state:
                            a theme toggle is a utility control, not a nav destination,
                            so it doesn't belong inside the expandable link list. */}
                        <ThemeToggle />

                        <motion.button
                            className="text-mist-100 md:hidden"
                            onClick={handleClick}
                            whileTap={{ scale: 0.9 }}
                            aria-label="Toggle navigation menu"
                        >
                            {click ? <FaTimes size={22} /> : <FaBars size={22} />}
                        </motion.button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {click && (
                    <motion.ul
                        className="fixed inset-0 z-30 flex flex-col items-center justify-center gap-8 bg-ink-950/95 font-mono text-2xl text-mist-100 backdrop-blur-md md:hidden"
                        initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: reducedMotion ? 1 : 0.96 }}
                        transition={reducedMotion ? { duration: 0.15 } : springs.snappy}
                        onClick={closeMenu}
                    >
                        {navLinkItems}
                    </motion.ul>
                )}
            </AnimatePresence>

            {notHome && <div className="h-[65px] w-full bg-ink-950" />}
        </div>
    )
}

export default Navbar
